import { useState, useCallback } from "react";
import { RegistrationFeeShareResponseType } from "../types/registrationFeeShareResponse";
import { showToast } from "../../../../core/utils/alert";
import {
  useGetAllRegistrationFeeSharesQuery,
  useAddRegistrationFeeShareMutation,
  useGetRegistrationFeeShareByIdQuery,
  useUpdateRegistrationFeeShareMutation,
  useDeleteRegistrationFeeShareMutation,
} from "../api/registrationFeeShareApi";

export const useRegistrationFeeShare = (
  initialParams = { size: 10, page: 1 }
) => {
  const [searchParams, setSearchParams] = useState({
    size: initialParams.size,
    page: initialParams.page,
    search: "",
    session_id: undefined as number | undefined,
    course_id: undefined as number | undefined,
    type_id: undefined as number | undefined,
    share_type: undefined as string | undefined,
  });

  // Use RTK Query hooks
  const {
    data: apiResponse,
    isLoading,
    isFetching,
    isError,
    error: queryError,
    refetch,
  } = useGetAllRegistrationFeeSharesQuery(searchParams);

  const [addFeeShareMutation, { isLoading: isAdding }] =
    useAddRegistrationFeeShareMutation();
  const [updateFeeShareMutation, { isLoading: isUpdating }] =
    useUpdateRegistrationFeeShareMutation();
  const [deleteFeeShareMutation, { isLoading: isDeleting }] =
    useDeleteRegistrationFeeShareMutation();

  // Get fee share by ID
  const useGetFeeShareById = (id: string) => {
    const {
      data: response,
      isLoading: loading,
      refetch,
    } = useGetRegistrationFeeShareByIdQuery({ id }, { skip: !id });

    console.log("Retrieved fee share data:", response?.data); // Add debugging log

    // Create a type assertion to handle both property names in the response
    const responseData = response?.data as
      | {
          feeShare?: RegistrationFeeShareResponseType;
          registrationfeeshare?: RegistrationFeeShareResponseType;
        }
      | undefined;

    return {
      feeShare: responseData?.feeShare || responseData?.registrationfeeshare,
      loading,
      refetch,
    };
  };

  // Add fee share
  const addFeeShare = async (payload: any) => {
    try {
      const result = await addFeeShareMutation(payload).unwrap();
      if (result.success) {
        showToast({
          title: "Success",
          text: result.message || "Registration fee share created successfully",
          icon: "success",
        });
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Failed to add registration fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Update fee share
  const updateFeeShare = async (id: string, payload: any) => {
    try {
      const result = await updateFeeShareMutation({ id, payload }).unwrap();
      if (result.success) {
        showToast({
          title: "Success",
          text: result.message || "Registration fee share updated successfully",
          icon: "success",
        });
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Failed to update registration fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Delete fee share
  const deleteFeeShare = async (id: string) => {
    try {
      const result = await deleteFeeShareMutation({ id }).unwrap();
      if (result.success) {
        showToast({
          title: "Success",
          text: result.message || "Registration fee share deleted successfully",
          icon: "success",
        });
        return { success: true };
      }
      return { success: false };
    } catch (error: any) {
      showToast({
        title: "Error",
        text: error.message || "Failed to delete registration fee share",
        icon: "error",
      });
      return { success: false, error };
    }
  };

  // Search and pagination handlers
  const handleSearch = (search: string) => {
    setSearchParams((prev) => ({ ...prev, search, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setSearchParams((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setSearchParams((prev) => ({ ...prev, size: pageSize, page: 1 }));
  };

  const handleFilterChange = (filters: {
    session_id?: number;
    course_id?: number;
    type_id?: number;
    share_type?: string;
  }) => {
    setSearchParams((prev) => ({
      ...prev,
      ...filters,
      page: 1,
    }));
  };

  // Extract data and pagination information from the API response
  const feeShares = apiResponse?.data?.items || [];

  // Prepare pagination data from the API response
  const paginationData = apiResponse?.data
    ? {
        currentPage: apiResponse.data.currentPage,
        pageSize: apiResponse.data.pageSize,
        totalItems: apiResponse.data.totalItems,
        totalPages: apiResponse.data.totalPages,
      }
    : {
        currentPage: searchParams.page,
        pageSize: searchParams.size,
        totalItems: 0,
        totalPages: 0,
      };

  return {
    // Data
    feeShares,
    paginationData,
    searchTerm: searchParams.search,

    // Loading states
    isLoading: isLoading || isFetching,
    isAdding,
    isUpdating,
    isDeleting,
    isError,
    error: queryError,

    // Actions
    refetch,
    handleSearch,
    handlePageChange,
    handlePageSizeChange,
    handleFilterChange,
    addFeeShare,
    updateFeeShare,
    deleteFeeShare,
    useGetFeeShareById,

    // Current filter state
    filters: {
      session_id: searchParams.session_id,
      course_id: searchParams.course_id,
      type_id: searchParams.type_id,
      share_type: searchParams.share_type,
    },
  };
};
